import { HttpClient, HttpParams } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { map, Observable } from 'rxjs';
import { environment } from '../../../environments/environment';

import { DeliveryNote, DeliveryNoteSearchCommand, S3Object } from '../../models/delivery-note/delivery-note';
import { ApiResponse } from '@card-software/nexus-utilities';

@Injectable({
  providedIn: 'root',
})
export class DeliveryNoteProxyService {
  private readonly httpClient = inject(HttpClient);
  private readonly baseUrl = environment.proxyUrls.orderApi + '/api/v1/delivery-note';

  public getDeliveryNotes(): Observable<ApiResponse<DeliveryNote[]>> {
    return this.httpClient.get<ApiResponse<DeliveryNote[]>>(`${this.baseUrl}/all`);
  }

  public getDeliveryNote(id: string): Observable<ApiResponse<DeliveryNote>> {
    return this.httpClient.get<ApiResponse<DeliveryNote>>(`${this.baseUrl}/${id}`);
  }

  public createDeliveryNote(data: DeliveryNote): Observable<ApiResponse<DeliveryNote>> {
    return this.httpClient.post<ApiResponse<DeliveryNote>>(this.baseUrl, data);
  }

  public updateDeliveryNote(id: string, data: DeliveryNote): Observable<ApiResponse<object>> {
    return this.httpClient.put<ApiResponse<object>>(`${this.baseUrl}/${id}`, data);
  }

  public deleteDeliveryNote(id: string): Observable<ApiResponse<object>> {
    return this.httpClient.delete<ApiResponse<object>>(`${this.baseUrl}/${id}`);
  }

  public getDeliveryNotesByIds(ids: string[]): Observable<ApiResponse<DeliveryNote[]>> {
    return this.httpClient.post<ApiResponse<DeliveryNote[]>>(`${this.baseUrl}/batch/ids`, ids);
  }

  public uploadFiles(files: File[], deliveryNoteId: string): Observable<S3Object[]> {
    const formData = new FormData();
    files.forEach((file) => {
      formData.append('files', file, file.name);
    });

    return this.httpClient
      .post<{ success: boolean; data: S3Object[]; messageError: string }>(`${this.baseUrl}/upload-files/${deliveryNoteId}`, formData)
      .pipe(map(({ data }) => data));
  }

  public deleteFiles(files: S3Object[], deliveryNoteId: string): Observable<string> {
    return this.httpClient.post(`${this.baseUrl}/delete-files/${deliveryNoteId}`, files, { responseType: 'text' });
  }

  public searchDeliveryNotes(searchCommand: DeliveryNoteSearchCommand, matchAny = false): Observable<ApiResponse<DeliveryNote[]>> {
    const params = new HttpParams().set('matchAny', matchAny);
    return this.httpClient.post<ApiResponse<DeliveryNote[]>>(`${this.baseUrl}/search`, searchCommand, { params });
  }

  public getDeliveryNotesByWorkOrder(workOrderReference: string): Observable<ApiResponse<DeliveryNote[]>> {
    return this.httpClient.get<ApiResponse<DeliveryNote[]>>(`${this.baseUrl}/work-order/${workOrderReference}`);
  }
}
